'use client';

import { isBefore } from 'date-fns';
import { useState } from 'react';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import SelectableQuantityCounter from '../../../../components/ui/SelectableQuantityCounter/SelectableQuantityCounter';
import { useTradeContext } from '../../../../components/ui/TradePageClient/TradePageClient';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { newDate } from '../../../../utils/date';
import type { LotteryOptionGoodsForm } from '../../types';
import { createStagingInfo } from '../../utils';
import styles from './OptionCollectionList.module.scss';

// エラーメッセージ定数
const INVALID_CHARACTER_ERROR_MESSAGE =
  '利用できない文字（記号など）が含まれています。ひらがな、カタカナ、漢字、アルファベット（半角・全角）のみが利用可能です。';

export type OptionCollectionListProps = {
  optionFormData: LotteryOptionGoodsForm[];
  isEditMode?: boolean;
  step?: 'quantity' | 'name';
  onStepChange?: (step: 'quantity' | 'name') => void;
  optionDefaultSetType: LotteryOptionGoodsForm['optionDefaultSetType'];
};

function OptionCollectionList({
  optionFormData,
  isEditMode = false,
  step,
  onStepChange: _onStepChange,
  optionDefaultSetType,
}: OptionCollectionListProps) {
  const { selectedItems } = useTradeContext();
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>({});

  // 入力値のバリデーション関数
  const validateInput = (value: string): boolean => {
    // ひらがな、カタカナ、漢字、数字（全角・半角）、アルファベット（半角・全角）のみを許可
    const validPattern = /^[ぁ-んァ-ヶ一-龥々0-9０-９a-zA-Zａ-ｚＡ-Ｚ\s]*$/;
    return validPattern.test(value);
  };

  // 入力値変更時のハンドラー
  const handleInputChange = (inputKey: string, value: string) => {
    const isValid = validateInput(value);
    setInputErrors((prev) => ({
      ...prev,
      [inputKey]: !isValid,
    }));
  };
  const formattedItems = (items: Array<LotteryOptionGoodsForm>) => {
    const result = items.map((item) => {
      const stagingKey = item.label.stagingKey || '';
      const stagingInfo = createStagingInfo(stagingKey);

      const formattedItem = {
        ...item,
        ...stagingInfo,
      };
      return formattedItem;
    });

    return result;
  };

  // 期日チェック
  const isPastDeadline = (formItem: LotteryOptionGoodsForm) => {
    const now = newDate();
    const deadline = newDate(formItem.optionCutoffAt);
    return isBefore(deadline, now);
  };

  // 宛名の入力欄のプレースホルダーを取得する関数
  const getNamedInputPlaceholder = () => {
    if (optionDefaultSetType === 'name') return 'ご希望の宛名を入力してください';
    return 'ご希望の内容を入力してください';
  };

  // 「宛名選択式オプション」の数量入力UIを表示すべきかを判定する。
  // - オプション種別が `named_selectable` であること
  // - 画面ステップが `quantity` であること
  // - 申込期限前であること（期限後は数量編集させない）
  const isNamedSelectableQuantityStep = (formItem: LotteryOptionGoodsForm) => {
    if (formItem.optionInfo.type !== 'named_selectable') return false;
    if (step !== 'quantity') return false;
    return !isPastDeadline(formItem);
  };

  // optionInfo配下の配列をレンダリングする関数
  const renderOptionItems = (formItem: LotteryOptionGoodsForm, editMode: boolean) => {
    const { optionInfo } = formItem;
    const isDeadlinePassed = isPastDeadline(formItem);
    const currentStep = formItem.optionInfo.type === 'named_selectable' && isDeadlinePassed ? 'name' : step;

    // 自動入力数を表示するかどうかを判定する関数
    const shouldShowAutoInputCount = () => {
      if (!isDeadlinePassed) return false;
      if (!formItem.optionInfo.named) return false;
      return formItem.optionInfo.named.some((item) => item.isSystemInput === true);
    };

    switch (optionInfo.type) {
      case 'named':
        return (
          <div key={`named-${formItem.id}`} className={`${styles.itemCardNamed}`}>
            <div className={styles.itemCardNamedInner}>
              <div className={`${styles.itemDetailNamedWrapper} ${utilsStyles.mtXSmall}`}>
                <div className={styles.itemImage}>
                  <ImageView alt={formItem.name} src={formItem.imageUrl} />
                </div>
                <div className={`${styles.itemDetailNamed} ${utilsStyles.mlSmall}`}>
                  <div className={styles.stagingRankWrapper}>{staging(formItem)}</div>
                  <div className={styles.itemNameWrapper}>
                    <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.label.name}</p>
                    <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.name}</p>
                  </div>
                  <div className={`${styles.autoInputMessageNamedWrapper} ${utilsStyles.mtXSmall} `}>
                    {shouldShowAutoInputCount() && (
                      <div
                        className={`${utilsStyles.textSmall} ${utilsStyles.textBold}  ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                      >
                        自動入力数：
                        {formItem.optionInfo.named?.filter((item) => item.isSystemInput === true).length || 0}個
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.optionInputNamedWrapper}>
                {formItem.totalAcquiredCount > 1 && (
                  <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                    ※同一の内容をご希望の場合も、
                    <span className={`${utilsStyles.primaryColor} ${utilsStyles.textBold}`}>入力欄１個につき１点</span>
                    の入力をお願いいたします
                  </p>
                )}
                {Array.from({ length: formItem.totalAcquiredCount }, (_, inputIndex) => {
                  // 既存の入力内容を取得
                  const namedItem = optionInfo.named?.[inputIndex];
                  const existingValue = namedItem?.value || '';
                  const isSystemInput = namedItem?.isSystemInput || false;
                  const inputKey = `named-${formItem.id}-${inputIndex}`;
                  const hasError = inputErrors[inputKey];
                  const displayValue =
                    isDeadlinePassed && isSystemInput && existingValue ? `【自動入力】${existingValue}` : existingValue;
                  const optionInputTitle = `オプション入力欄（${inputIndex + 1}/${formItem.totalAcquiredCount}）`;

                  return (
                    <div key={`${formItem.id}-${inputIndex}`} className={styles.inputContainer}>
                      <div className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                        {optionInputTitle}
                      </div>
                      {hasError && <div className={styles.errorMessage}>{INVALID_CHARACTER_ERROR_MESSAGE}</div>}
                      <textarea
                        key={`input-${formItem.id}-${inputIndex}-${existingValue}`}
                        name={`optionInput-${formItem.id}-${inputIndex}`}
                        placeholder={getNamedInputPlaceholder()}
                        className={`${styles.optionInput} ${editMode && isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                        defaultValue={displayValue}
                        onChange={(e) => handleInputChange(inputKey, e.target.value)}
                        disabled={editMode && isDeadlinePassed}
                        rows={3}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'selectable':
        if (optionInfo.selectable && optionInfo.selectable.length > 0) {
          return optionInfo.selectable.map((selectableItem, index) => {
            const isFirstSelectableItem: boolean = index === 0;

            // 自動入力数を表示するかどうかを判定する関数
            const isAutoInput = () => {
              // 入力期限を迎えていない場合は非表示
              if (!isDeadlinePassed) return false;

              // システムインプットがされていない場合は非表示
              if (!selectableItem.isSystemInput) return false;

              return true;
            };

            // 期限切れの場合のスタイルを取得する関数
            const getWrapperQuantityPastDeadlineClassName = () => {
              if (isAutoInput() || isDeadlinePassed) return styles.wrapperQuantityPastDeadline;
              return '';
            };

            return [
              isFirstSelectableItem ? (
                <div
                  key={`selectable-header-${formItem.id}`}
                  className={`${styles.itemCardHeaderQuantity} ${styles.optionItemGroupQuantityHeaderItem}`}
                >
                  <div className={styles.itemCardHeaderQuantityInner}>
                    <div className={`${styles.itemDetailHeaderQuantityWrapper}`}>
                      <div className={`${styles.itemDetailHeaderQuantity} ${utilsStyles.mlSmall}`}>
                        <div className={styles.stagingRankHeaderQuantityWrapper}>{stagingHeader(formItem)}</div>
                        <div className={styles.itemNameHeaderQuantityWrapper}>
                          <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>
                            {formItem.label.name}
                          </p>
                        </div>
                        <div className={styles.itemNameHeaderQuantityWrapper}>
                          <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.name}</p>
                        </div>
                        <div className={styles.acquiredCountHeaderQuantityWrapper}>
                          <p className={`${styles.acquiredCountHeaderQuantity}`}>
                            保有数：{formItem.totalAcquiredCount}個
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null,
              <div
                key={`selectable-${formItem.id}-${index}`}
                className={`${styles.itemCardQuantity} ${styles.optionItemGroupQuantityItem}`}
              >
                <div className={styles.itemCardQuantityInner}>
                  <div className={`${styles.itemDetailQuantityWrapper} ${utilsStyles.mtXSmall}`}>
                    <div className={styles.itemImageQuantity}>
                      <ImageView
                        alt={selectableItem.label || formItem.name}
                        src={(selectableItem as { imageUrl?: string }).imageUrl || formItem.imageUrl}
                      />
                    </div>
                    <div className={`${styles.itemDetailQuantity} ${utilsStyles.mlSmall}`}>
                      <div className={styles.itemNameWrapper}>
                        <p className={`${styles.itemNameQuantity} ${utilsStyles.textBold}`}>
                          {selectableItem.label || 'オプション選択項目'}
                        </p>
                      </div>
                      <div className={`${styles.wrapperQuantity} ${getWrapperQuantityPastDeadlineClassName()}`}>
                        <div className={`${utilsStyles.centered} ${styles.quantityCounterWrapper}`}>
                          <SelectableQuantityCounter
                            goodId={`${formItem.id}-${index}`}
                            acquiredCount={formItem.totalAcquiredCount}
                            disabled={false}
                            tradeableInCount={formItem.totalAcquiredCount}
                            totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                              (sum, current) => sum + current,
                              0,
                            )}
                            initialQuantity={selectableItem.value ? Number(selectableItem.value) : 0}
                            isPastDeadline={isDeadlinePassed}
                            isAutoInput={isAutoInput()}
                          />
                        </div>
                      </div>
                      <div className={styles.autoInputMessageQuantityWrapper}>
                        {isAutoInput() && (
                          <div
                            className={`${utilsStyles.textSmall} ${utilsStyles.textBold} ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                          >
                            自動入力数：{selectableItem.value}個
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
            ];
          });
        }
        break;

      case 'named_selectable':
        if (!optionInfo.namedSelectable) return <></>;
        // ステップが'quantity'の場合はselectableと同じ表示
        if (currentStep === 'quantity') {
          return optionInfo.namedSelectable.map((namedSelectableItem, index) => {
            const isFirstNamedSelectableItem: boolean = index === 0;
            // 各景品の選択数量を取得
            const goodId = `${formItem.id}-${index}`;
            const selectedQuantity = selectedItems.get(goodId) || 0;

            const isAutoInput = () => {
              // 入力期限を迎えていない場合は非表示
              if (!isDeadlinePassed) return false;

              // システムインプットがされていない場合は非表示
              if (!namedSelectableItem.isSystemInput) return false;

              // value未設定の場合は非表示
              if (namedSelectableItem.value === '') return false;

              // valueが0以外なら表示、0なら非表示
              return namedSelectableItem.value !== '0';
            };

            // 期限切れの場合のスタイルを取得する関数
            const getWrapperQuantityPastDeadlineClassName = () => {
              if (isAutoInput() || isDeadlinePassed) return styles.wrapperQuantityPastDeadline;
              return '';
            };

            return [
              isFirstNamedSelectableItem ? (
                <div
                  key={`selectable-header-${formItem.id}`}
                  className={`${styles.itemCardHeaderQuantity} ${styles.optionItemGroupQuantityHeaderItem}`}
                >
                  <div className={styles.itemCardHeaderQuantityInner}>
                    <div className={`${styles.itemDetailHeaderQuantityWrapper}`}>
                      <div className={`${styles.itemDetailHeaderQuantity} ${utilsStyles.mlSmall}`}>
                        <div className={styles.stagingRankHeaderQuantityWrapper}>{stagingHeader(formItem)}</div>
                        <div className={styles.itemNameHeaderQuantityWrapper}>
                          <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>
                            {formItem.label.name}
                          </p>
                        </div>
                        <div className={styles.itemNameHeaderQuantityWrapper}>
                          <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.name}</p>
                        </div>
                        <div className={styles.acquiredCountHeaderQuantityWrapper}>
                          <p className={`${styles.acquiredCountHeaderQuantity}`}>
                            保有数：{formItem.totalAcquiredCount}個
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null,
              // selectableケースと同じ表示ロジック
              <div
                key={`selectable-${formItem.id}-${index}`}
                className={`${styles.itemCardQuantity} ${styles.optionItemGroupQuantityItem}`}
              >
                <div className={styles.itemCardQuantityInner}>
                  <div className={`${styles.itemDetailQuantityWrapper} ${utilsStyles.mtXSmall}`}>
                    <div className={styles.itemImageQuantity}>
                      <ImageView
                        alt={namedSelectableItem.label || formItem.name}
                        src={(namedSelectableItem as { imageUrl?: string }).imageUrl || formItem.imageUrl}
                      />
                    </div>
                    <div className={`${styles.itemDetailQuantity} ${utilsStyles.mlSmall}`}>
                      <div className={styles.itemNameWrapper}>
                        <p className={`${styles.itemNameQuantity} ${utilsStyles.textBold}`}>
                          {namedSelectableItem.label || 'オプション選択項目'}
                        </p>
                      </div>
                      <div className={`${styles.wrapperQuantity} ${getWrapperQuantityPastDeadlineClassName()}`}>
                        <div className={`${utilsStyles.centered} ${styles.quantityCounterWrapper}`}>
                          <SelectableQuantityCounter
                            goodId={`${formItem.id}-${index}`}
                            acquiredCount={formItem.totalAcquiredCount}
                            disabled={false}
                            tradeableInCount={formItem.totalAcquiredCount}
                            totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                              (sum, current) => sum + current,
                              0,
                            )}
                            initialQuantity={namedSelectableItem.value ? Number(namedSelectableItem.value) : 0}
                            isPastDeadline={isDeadlinePassed}
                            isAutoInput={isAutoInput()}
                          />
                        </div>
                      </div>
                      <div className={styles.autoInputMessageQuantityWrapper}>
                        {isAutoInput() && (
                          <div
                            className={`${utilsStyles.textSmall} ${utilsStyles.textBold} ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                          >
                            自動入力数：{namedSelectableItem.value}個
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {isDeadlinePassed && (
                    <>
                      {/* 期限切れの場合、2ページ目は進めないため、例外的に1ページ目に宛名の入力欄を表示(編集不可) */}
                      <div className={styles.optionInputNamedWrapper}>
                        {Array.from({ length: selectedQuantity }, (_, inputIndex) => {
                          // 既存の入力内容を取得（namedSelectableのnotesから）
                          const noteItem = namedSelectableItem.notes?.[inputIndex];
                          const existingValue = noteItem?.value || '';
                          const isSystemInput = noteItem?.isSystemInput || false;
                          const inputKey = `named_selectable-${formItem.id}-${index}-${inputIndex}`;
                          const hasError = inputErrors[inputKey];
                          const buildDefaultValue = () => {
                            if (!isDeadlinePassed) return existingValue;
                            if (!isSystemInput) return existingValue;
                            if (!existingValue) return existingValue;
                            return `【自動入力】${existingValue}`;
                          };

                          return (
                            <div key={`${formItem.id}-${index}-${inputIndex}`} className={styles.inputContainer}>
                              {hasError && <div className={styles.errorMessage}>{INVALID_CHARACTER_ERROR_MESSAGE}</div>}
                              <textarea
                                key={`input-${formItem.id}-${index}-${inputIndex}-${existingValue}`}
                                name={`optionInput-${formItem.id}-${index}-${inputIndex}`}
                                placeholder={getNamedInputPlaceholder()}
                                className={`${styles.optionInput} ${isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                                defaultValue={buildDefaultValue()}
                                onChange={(e) => handleInputChange(inputKey, e.target.value)}
                                disabled={true}
                                rows={3}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>,
            ];
          });
        }

        // ステップが'name'の場合は選択した数量分の入力欄を表示
        if (currentStep === 'name') {
          return optionInfo.namedSelectable.map((namedSelectableItem, index) => {
            // 各景品の選択数量を取得
            const goodId = `${formItem.id}-${index}`;
            const selectedQuantity = selectedItems.get(goodId) || 0;

            // 選択数量が0の場合、かつ、入力期限を迎えていない場合は処理を走らせても意味がないので表示しない
            // 入力期限を迎えたら、selectedQuantityがずっと0のままだが、後続の処理を無条件に走らせる必要があるため
            if (selectedQuantity === 0 && !isDeadlinePassed) {
              return null;
            }

            const autoInputCount = namedSelectableItem.notes?.filter((item) => item.isSystemInput).length || 0;

            const isAutoInput = () => {
              // 入力期限を迎えていない場合は非表示
              if (!isDeadlinePassed) return false;

              // 自動入力数が0の場合は非表示
              if (autoInputCount === 0) return false;

              // notesが存在しない場合は非表示
              const notes = namedSelectableItem.notes;
              if (!notes) return false;

              // notesが存在して、システムインプットされている場合は表示、それ以外は非表示
              return notes.some((item) => item.isSystemInput);
            };

            return (
              <div key={`named_selectable-${formItem.id}-${index}`} className={`${styles.itemCardNamed}`}>
                <div className={styles.itemCardNamedInner}>
                  <div className={`${styles.itemDetailNamedWrapper} ${utilsStyles.mtXSmall}`}>
                    <div className={styles.itemImage}>
                      <ImageView
                        alt={namedSelectableItem.label || formItem.name}
                        src={namedSelectableItem.imageUrl || formItem.imageUrl}
                      />
                    </div>
                    <div className={`${styles.itemDetailNamed} ${utilsStyles.mlSmall}`}>
                      <div className={styles.stagingRankWrapper}>{staging(formItem)}</div>
                      <div className={styles.itemNameWrapper}>
                        <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.name}</p>
                        <p className={`${styles.itemName} ${utilsStyles.textBold}`}>
                          {namedSelectableItem.label || 'オプション入力・選択項目'}
                        </p>
                      </div>

                      <div className={styles.desiredQuantityWrapper}>
                        {isAutoInput() ? (
                          <div className={styles.desiredQuantityAutoInputLabel}>
                            自動入力数：
                            {autoInputCount}個
                          </div>
                        ) : (
                          <p className={`${styles.desiredQuantityLabel} ${utilsStyles.textBold}`}>希望する個数</p>
                        )}
                        <div className={styles.desiredQuantityCounter}>
                          {isDeadlinePassed ? (
                            // 期限切れ後はサーバーから取得した個数をそのまま表示する
                            <SelectableQuantityCounter
                              goodId={`${formItem.id}-${index}`}
                              acquiredCount={formItem.totalAcquiredCount}
                              // 選べる＆宛名オプションの宛名UIでもサーバーから取得した数量をカウンターとして表示する必要がある。
                              // ただし、カウンターの数値は常に変更できないようにする必要がある。
                              // そのため、強制的に無効にすることでカウンターの数値を変更できないようにできる。
                              disabled={true}
                              tradeableInCount={formItem.totalAcquiredCount}
                              totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                                (sum, current) => sum + current,
                                0,
                              )}
                              initialQuantity={namedSelectableItem.value ? Number(namedSelectableItem.value) : 0}
                              isPastDeadline={isDeadlinePassed}
                              isAutoInput={isAutoInput()}
                            />
                          ) : (
                            // 期限切れ前はquantityステップで選択した個数をそのまま表示する
                            <SelectableQuantityCounter
                              goodId={`${formItem.id}-${index}`}
                              acquiredCount={selectedQuantity}
                              // 選べる＆宛名オプションの宛名UIでも選べるUIで選択した数量をカウンターとして表示する必要がある。
                              // ただし、カウンターの数値は常に変更できないようにする必要がある。
                              // そのため、強制的に無効にすることでカウンターの数値を変更できないようにできる。
                              disabled={true}
                              tradeableInCount={selectedQuantity}
                              totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                                (sum, current) => sum + current,
                                0,
                              )}
                              initialQuantity={selectedQuantity ? Number(selectedQuantity) : 0}
                              isPastDeadline={isDeadlinePassed}
                              isAutoInput={isAutoInput()}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.optionInputNamedWrapper}>
                    {selectedQuantity > 1 && (
                      <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                        ※同一の内容をご希望の場合も、
                        <span className={`${utilsStyles.primaryColor} ${utilsStyles.textBold}`}>
                          入力欄１個につき１点
                        </span>
                        の入力をお願いいたします
                      </p>
                    )}
                    {Array.from({ length: selectedQuantity }, (_, inputIndex) => {
                      // 既存の入力内容を取得（namedSelectableのnotesから）
                      const noteItem = namedSelectableItem.notes?.[inputIndex];
                      const existingValue = noteItem?.value || '';
                      const isSystemInput = noteItem?.isSystemInput || false;
                      const inputKey = `named_selectable-${formItem.id}-${index}-${inputIndex}`;
                      const hasError = inputErrors[inputKey];
                      const displayValue =
                        isDeadlinePassed && isSystemInput && existingValue
                          ? `【自動入力】${existingValue}`
                          : existingValue;
                      const optionInputTitle = `オプション入力欄（${inputIndex + 1}/${selectedQuantity}）`;

                      return (
                        <div key={`${formItem.id}-${index}-${inputIndex}`} className={styles.inputContainer}>
                          <div className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                            {optionInputTitle}
                          </div>
                          {hasError && <div className={styles.errorMessage}>{INVALID_CHARACTER_ERROR_MESSAGE}</div>}
                          <textarea
                            key={`input-${formItem.id}-${index}-${inputIndex}-${existingValue}`}
                            name={`optionInput-${formItem.id}-${index}-${inputIndex}`}
                            placeholder={getNamedInputPlaceholder()}
                            className={`${styles.optionInput} ${editMode && isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                            defaultValue={displayValue}
                            onChange={(e) => handleInputChange(inputKey, e.target.value)}
                            disabled={editMode && isDeadlinePassed}
                            rows={3}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          });
        }

        break;

      default:
        return null;
    }

    return null;
  };

  return (
    <div className={styles.optionCollectionList}>
      {formattedItems(optionFormData).map((formItem) => (
        <div
          key={formItem.id}
          className={
            formItem.optionInfo.type === 'selectable' || isNamedSelectableQuantityStep(formItem)
              ? styles.optionItemGroupQuantity
              : undefined
          }
        >
          {renderOptionItems(formItem, isEditMode)}
        </div>
      ))}
    </div>
  );
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    default:
      return styles.defaultRankImage;
  }
}

function getRankStyleHeader(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1HeaderQuantity;
    case 2:
      return styles.rank2HeaderQuantity;
    default:
      return styles.defaultRankImageHeaderQuantity;
  }
}

function staging(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

function stagingHeader(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitHeaderQuantityWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRankHeaderQuantity} ${getRankStyleHeader(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingTextHeaderQuantity} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default OptionCollectionList;
